import { Box, alpha, styled } from '@mui/material';
import { CartTotalIcon } from '../icons';
import { Typography } from '@chargeafter/ui';
import NumberFormat from 'react-number-format';

const StyledOutlinedBox = styled(Box)(({ theme }) => ({
  borderRadius: 8,
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: theme.palette.grey[200],
  width: '100%',
}));

const StyledCartNumberBox = styled(Box)<{ showError?: boolean }>(
  ({ theme, showError }) => ({
    padding: theme.spacing(1, 0),
    fontSize: 38,
    color: showError ? theme.palette.error.dark : theme.palette.primary.main,
    fontWeight: 500,
    '& > input': {
      textAlign: 'center',
    },
  }),
);

const StyledCartDetailsTypography = styled(Typography)<{
  showError?: boolean;
}>(({ theme, showError }) => ({
  padding: theme.spacing(0.5, 0.5),
  color: showError ? theme.palette.error.dark : theme.palette.primary.main,
  backgroundColor: showError
    ? theme.palette.error.light
    : theme.palette.secondary.light,
}));

interface CartTotalProps {
  showError?: boolean;
  totalAmount?: string | number;
  quantity?: number;
  allowNegative?: boolean;
  errorMessage?: string;
}

const StyledNumberFormat = styled(NumberFormat)({
  outline: 'none',
  border: 'none',
  font: 'inherit',
  padding: 0,
  color: 'inherit',
  width: '100%',
  lineHeight: 'normal',
  '&::placeholder': {
    color: alpha('#000', 0.26),
  },
});

export const CartTotal = ({
  showError,
  totalAmount,
  quantity,
  errorMessage = 'Cart total exceeding',
  allowNegative = false,
}: CartTotalProps) => {
  return (
    <StyledOutlinedBox>
      <StyledCartDetailsTypography
        data-testid="cart-total-header"
        align="center"
        showError={showError}
      >
        <CartTotalIcon
          color="inherit"
          style={{ top: 5, position: 'relative' }}
        />
        <Box width={10} display="inline-block" component="span" />
        {showError ? (
          <>{errorMessage}</>
        ) : (
          <>Cart Total {quantity && `(${quantity} items)`}</>
        )}
      </StyledCartDetailsTypography>
      <StyledCartNumberBox showError={showError}>
        <StyledNumberFormat
          isNumericString
          readOnly
          fixedDecimalScale
          data-testid="cart-input"
          value={totalAmount ?? ''}
          placeholder="$00.00"
          thousandSeparator
          prefix="$"
          allowNegative={allowNegative}
          decimalScale={2}
        />
      </StyledCartNumberBox>
    </StyledOutlinedBox>
  );
};
